import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../../../../core';
import { EnumTipoSegmento } from '../../../../../domains/shared/components/segmentos-multi-select/enums/segmento-tipo.enum';
import { EnumSituacao } from '../../../../../domains/shared/enums/situacao.enum';
import { EnumCadastro } from '../../../../../domains/shared/interfaces/cadastro.enum';
import { ClienteFilterStorageService } from '../cliente-filter-storage.service';
import { countFiltrosAvancados, IFiltrosAvancados } from '../filtros-avancados';
import { ClienteFilter } from './cliente-filter';

@Component({
  selector: 'sgt-novo-cliente-filter',
  templateUrl: './novo-cliente-filter.component.html',
  styleUrls: ['./novo-cliente-filter.component.scss']
})
export class NovoClienteFilterComponent implements OnInit, OnDestroy {
  @Output() filterInitialized = new EventEmitter<ClienteFilter>();
  @Output() filterChange = new EventEmitter<ClienteFilter>();

  quickForm: FormGroup;

  tipoSegmento = EnumTipoSegmento;
  isCadastroItem = EnumCadastro.CLIENTE;
  situacao = EnumSituacao.ATIVO;
  advancedFilterCount = 0;

  isRepresentante: boolean;
  isAdvancedFilterModalOpen = false;

  private filter: ClienteFilter;
  advancedForm: Partial<IFiltrosAvancados>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private clienteFilterStorageService: ClienteFilterStorageService
  ) {}

  ngOnInit() {
    this.isRepresentante = this.authService.isRepresentante();
    this.quickForm = this.fb.group({
      filterPsq: '',
      cadastros: [],
      situacoes: [],
      idsClassificacoes: [],
      idsTags: [],
      idsRepresentantes: []
    });
    this.restoreFromStorage();
    this.buildFilter();
    this.filterInitialized.emit(this.filter);

    this.quickForm.get('filterPsq').valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => this.emitFilter());

  }

  get idsRepresentantes(): number[] {
    return (this.quickForm.get('idsRepresentantes').value as number[]) ?? [];
  }

  openAdvancedFilterModal() {
    this.isAdvancedFilterModalOpen = true;
  }

  onAdvancedFilterApply(value: IFiltrosAvancados) {
    this.advancedForm = value;
    this.advancedFilterCount = countFiltrosAvancados(value);
    this.isAdvancedFilterModalOpen = false;
    this.emitFilter();
  }

  onAdvancedFilterCleared() {
    this.advancedForm = null;
    this.advancedFilterCount = 0;
    this.isAdvancedFilterModalOpen = false;
    this.emitFilter();
  }

  onAdvancedFilterClosed() {
    this.isAdvancedFilterModalOpen = false;
  }

  onChangeCadastros(_: number[]) {
    this.emitFilter();
  }

  onChangeSituacoes(_: number[]) {
    this.emitFilter();
  }

  onChangeSegmentos(_: number[]) {
    this.emitFilter();
  }

  onChangeTags(_: number[]) {
    this.emitFilter();
  }

  onChangeRepresentantes(_: number[]) {
    this.emitFilter();
  }

  clearSearch() {
    this.quickForm.get('filterPsq').setValue('');
  }

  refresh() {
    this.emitFilter();
  }

  private restoreFromStorage(): void {
    const stored = new ClienteFilter();
    this.clienteFilterStorageService.applyFilter(stored);

    this.quickForm.patchValue({
      filterPsq: stored.filterPsq ?? '',
      cadastros: stored.cadastros ?? [],
      situacoes: stored.situacoes ?? [],
      idsClassificacoes: stored.idsClassificacoes ?? [],
      idsTags: stored.idsTags ?? [],
      idsRepresentantes: stored.idsRepresentantes ?? []
    }, { emitEvent: false });

    const advanced: Partial<IFiltrosAvancados> = {
      ...stored,
      idEstado: Array.isArray(stored.idEstado)
        ? stored.idEstado
        : stored.idEstado ? [stored.idEstado] : []
    };

    const count = countFiltrosAvancados(advanced);
    if (count > 0) {
      this.advancedForm = advanced;
      this.advancedFilterCount = count;
    }
  }

  private buildFilter(): void {
    const f = new ClienteFilter();
    Object.assign(f, this.advancedForm ?? {}, this.quickForm.getRawValue());
    f.idClienteCentralCompras = this.advancedForm?.clienteCentralCompras?.id;
    f.idCidade = this.advancedForm?.cidade?.id;
    this.filter = f;
  }

  private emitFilter() {
    this.buildFilter();
    this.filterChange.emit(this.filter);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
